import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { normalizeVerbCaps } from '../../utils/formAConverters';
import { requestTypeOf, REQUEST_TYPE_REGISTRY, VERB_DESCRIPTIONS } from '../../constants/formA';
import { DlrGrantCard } from '../SchemaGrantsPanel/SchemaGrantsPanel';
import YamlPreview from '../YamlPreview';
import SchemaIcon from '../common/SchemaIcon';
import { FileCode, Plus, X, SlidersHorizontal, Network, Zap, User, Link2, Users, Activity, Puzzle, Settings, Layers, ChevronRight } from 'lucide-react';
import { schemaLabel, findSchemaById } from '../../utils/schemaUtils';
import './RealmSchemaEditor.css';

// ── Data model mappings ─────────────────────────────────────────────────────

const VERB_MAP = {
  R: { resource: 'nodes', verb: 'read',         scopeKey: 'nodes:read',    label: 'Read'    },
  W: { resource: 'nodes', verb: 'create',        scopeKey: 'nodes:create', label: 'Create'  },
  E: { resource: 'nodes', verb: 'edit',          scopeKey: 'nodes:edit',         label: 'Edit'    },
  D: { resource: 'nodes', verb: 'delete',        scopeKey: 'nodes:delete', label: 'Delete'  },
  P: { resource: 'nodes', verb: 'propose',       scopeKey: 'nodes:propose',      label: 'Propose' },
};

const PILL_TOOLTIPS = {
  R: 'Read — view existing nodes',
  W: 'Create — add new nodes',
  E: 'Edit — modify existing nodes',
  D: 'Delete — remove existing nodes',
  P: 'Propose — suggest changes for approval',
};

// W (create_nodes) does not support node_ids in scope_filters
const NODE_ID_UNSUPPORTED = new Set(['W']);

// Relationships — See / Create / Edit / Delete
// Letter -> canonical request type. One map, used by both render sites: they
// disagreed about which verbs existed for as long as there were two of them.

// Activity log filter options (scope_filters['activity:read'])
const ACTIVITY_CATEGORIES = [
  { id: 'node',         label: 'Node changes'        },
  { id: 'app_access',   label: 'App data access'      },
  { id: 'data_read',    label: 'Data reads'           },
  { id: 'security',     label: 'Security events'      },
  { id: 'sharing',      label: 'Sharing events'       },
  { id: 'plugin',       label: 'Plugin executions'    },
  { id: 'automation',   label: 'Automations'          },
  { id: 'relationship', label: 'Relationship changes' },
];

const ACTIVITY_ACTOR_TYPES = [
  { id: 'third_party', label: 'Third-party apps'    },
  { id: 'user',        label: 'Your direct actions' },
  { id: 'system',      label: 'System'              },
];

// Quick presets for Identity Graph baseline
const QUICK_PRESETS = [
  {
    key: 'none', label: 'Nothing',
    perms: { R: false, W: false, E: false, D: false, P: false },
    adv:   {},
  },
  {
    key: 'read', label: 'Read only',
    perms: { R: true,  W: false, E: false, D: false, P: false },
    adv:   {},
  },
  {
    key: 'readwrite', label: 'Read & Write',
    perms: { R: true,  W: true,  E: true,  D: false, P: false },
    adv:   { 'plugins.execute': true },
  },
  {
    key: 'full', label: 'Full access',
    perms: { R: true,  W: true,  E: true,  D: true,  P: true },
    // MUST stay in step with PrivacyRealm.FULL_ACCESS_CEILING (backend
    // models/privacy.py), which authz/vocabulary.py asserts is complete. A realm is a
    // CEILING, so a verb this preset leaves off is clamped out of every grant beneath
    // it — and `applyPreset` starts every advanced perm at false, so anything missing
    // here is actively switched OFF rather than merely left alone.
    //
    // `discovery.realms`, both `automations` verbs and `sub_entity.declare` were all
    // absent, so choosing "Full access" produced a realm strictly narrower than the
    // platform's Full Access and silently withdrew four capabilities.
    adv:   {
      'plugins.execute': true, 'plugins.prepare': true,
      'discovery.friends': true, 'discovery.groups': true, 'discovery.realms': true,
      'activity.read': true,
      'automations.write': true, 'automations.execute': true,
      'sub_entity.declare': true,
    },
  },
];

// Actions permissions — Plugins first
const ADVANCED_PERMS_DEF = [
  {
    key: 'plugins',
    label: 'Plugins',
    pills: [
      { verb: 'execute', label: 'Run', tooltip: 'Execute plugins on your data' },
    ],
  },
  {
    key: 'discovery',
    label: 'Social Discovery',
    pills: [
      { verb: 'friends', label: 'Friends', tooltip: 'Discover your connections'     },
      { verb: 'groups',  label: 'Groups',  tooltip: 'Discover groups you belong to' },
      { verb: 'realms',  label: 'Realms',  tooltip: 'Discover realms you make discoverable' },
    ],
  },
  {
    key: 'activity',
    label: 'Activity',
    pills: [
      { verb: 'read', label: 'See log', tooltip: 'See the activity log' },
    ],
  },
  {
    key: 'automations',
    label: 'Automations',
    pills: [
      { verb: 'write',   label: 'Manage', tooltip: 'Create & manage automations (needs a data or plugin permission to act on)' },
      { verb: 'execute', label: 'Run',    tooltip: 'Trigger automations and let them run on a schedule' },
    ],
  },
  {
    key: 'sub_entity',
    label: 'Sub Entities',
    pills: [
      { verb: 'declare', label: 'Declare', tooltip: 'Let apps declare sub-entities bounded by this realm' },
    ],
  },
];

// ── Conversion helpers ──────────────────────────────────────────────────────

function parseState(verbCaps, scopeFilters, allSchemas, allTags = [], allNodes = []) {
  const vc = normalizeVerbCaps(verbCaps || {});
  const sf = scopeFilters || {};
  const globalPerms = { R: false, W: false, E: false, D: false, P: false };
  const schemaMap = new Map();
  const nodeMap = new Map();

  for (const [letter, { resource, verb, scopeKey }] of Object.entries(VERB_MAP)) {
    if (!(vc[resource] || []).includes(verb)) continue;
    const sf_entry = sf[scopeKey] || {};
    const sids = sf_entry.schema_ids;
    // W (create_nodes) doesn't support node_ids
    const nids = NODE_ID_UNSUPPORTED.has(letter) ? null : sf_entry.node_ids;

    const hasSids = sids && sids.length > 0;
    const hasNids = nids && nids.length > 0;

    if (!hasSids && !hasNids) {
      globalPerms[letter] = true;
    } else {
      if (hasSids) {
        for (const sid of sids) {
          const key = String(sid);
          if (!schemaMap.has(key)) {
            const s = findSchemaById(allSchemas, key);
            schemaMap.set(key, {
              id: key,
              // Never a bare '?' — an unresolvable id is worth naming as such,
              // and the full id stays on the row's tooltip.
              name: schemaLabel(s?.schema_name || s?.uri) || `Unknown schema ${key.slice(0, 8)}…`,
              uri: s?.schema_name || s?.uri || key,
              icon: s?.schema_metadata?.icon || null,
              missing: !s,
              perms: { R: false, W: false, E: false, D: false, P: false },
            });
          }
          schemaMap.get(key).perms[letter] = true;
        }
      }
      if (hasNids) {
        for (const nid of nids) {
          const key = String(nid);
          if (!nodeMap.has(key)) {
            const n = allNodes.find(n => String(n.id) === key);
            nodeMap.set(key, {
              id: key,
              name: n?.value_json?.title || n?.value_json?.name || `Node #${key}`,
              schema_name: n?.schema_name || null,
              perms: { R: false, W: false, E: false, D: false, P: false },
            });
          }
          nodeMap.get(key).perms[letter] = true;
        }
      }
    }
  }

  const advancedPerms = {};
  for (const section of ADVANCED_PERMS_DEF) {
    for (const { verb } of section.pills) {
      let isOn = (vc[section.key] || []).includes(verb);
      // For tags, 'discover' is also on if 'read' is present (See = discover+read)
      if (section.key === 'tags' && verb === 'discover' && !isOn) {
        isOn = (vc.tags || []).includes('read');
      }
      advancedPerms[`${section.key}.${verb}`] = isOn;
    }
    // Sub-pills
    if (section.subPills) {
      for (const { verb } of section.subPills) {
        advancedPerms[`${section.key}.${verb}`] = (vc[section.key] || []).includes(verb);
      }
    }
  }

  const activityCategories = sf['activity:read']?.categories || [];
  const activityActorTypes = sf['activity:read']?.actor_types || [];

  return {
    globalPerms,
    schemaRows: [...schemaMap.values()],
    nodeRows: [...nodeMap.values()],
    advancedPerms,
    activityCategories,
    activityActorTypes,
  };
}

function toOutput(globalPerms, schemaRows, nodeRows, advancedPerms, activityCategories, activityActorTypes) {
  const verb_caps = { nodes: [], plugins: [], discovery: [], activity: [], automations: [], sub_entity: [] };
  const scope_filters = {};

  for (const [letter, { resource, verb, scopeKey }] of Object.entries(VERB_MAP)) {
    const isGlobal = globalPerms[letter];
    const perSchemaIds = schemaRows.filter(s => s.perms[letter]).map(s => s.id);
    // W (create_nodes) doesn't support node_ids
    const perNodeIds = NODE_ID_UNSUPPORTED.has(letter)
      ? []
      : nodeRows.filter(n => n.perms[letter]).map(n => n.id);

    if (isGlobal) {
      if (!verb_caps[resource].includes(verb)) verb_caps[resource].push(verb);
    } else if (perSchemaIds.length > 0 || perNodeIds.length > 0) {
      if (!verb_caps[resource].includes(verb)) verb_caps[resource].push(verb);
      scope_filters[scopeKey] = {};
      if (perSchemaIds.length > 0) scope_filters[scopeKey].schema_ids = perSchemaIds;
      if (perNodeIds.length > 0) scope_filters[scopeKey].node_ids = perNodeIds;
    }
  }

  for (const section of ADVANCED_PERMS_DEF) {
    for (const { verb } of section.pills) {
      if (advancedPerms[`${section.key}.${verb}`]) {
        if (!verb_caps[section.key]) verb_caps[section.key] = [];
        if (!verb_caps[section.key].includes(verb)) verb_caps[section.key].push(verb);
        // See (discover) implies read for tags
        if (section.key === 'tags' && verb === 'discover' && !verb_caps.tags.includes('read')) {
          verb_caps.tags.push('read');
        }
      }
    }
    // Sub-pills (e.g. tags create/edit/delete)
    if (section.subPills) {
      for (const { verb } of section.subPills) {
        if (advancedPerms[`${section.key}.${verb}`]) {
          if (!verb_caps[section.key]) verb_caps[section.key] = [];
          if (!verb_caps[section.key].includes(verb)) verb_caps[section.key].push(verb);
        }
      }
    }
  }

  // Activity scope filters — only when activity is enabled
  if (advancedPerms['activity.read']) {
    const seeActivity = {};
    if (activityCategories.length > 0) seeActivity.categories = activityCategories;
    if (activityActorTypes.length > 0) seeActivity.actor_types = activityActorTypes;
    if (Object.keys(seeActivity).length > 0) scope_filters['activity:read'] = seeActivity;
  }

  return { verb_caps, scope_filters };
}

// ── Profile inline editor ────────────────────────────────────────────────────

function ProfileEditForm({ nodeId, valueJson, fields, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setDraft({ ...valueJson });
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(nodeId, draft);
      setEditing(false);
    } catch (e) {
      console.warn('Profile update failed:', e);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        className="btn rse-profile-edit-btn"
        onClick={startEdit}
      >
        Edit
      </button>
    );
  }

  return (
    <div className="rse-profile-edit-form">
      {fields.map(f => (
        <label key={f.key} className="rse-profile-edit-field">
          <span className="rse-profile-edit-field__label">{f.label}</span>
          {f.key === 'bio' ? (
            <textarea
              className="rse-profile-edit-field__input"
              value={draft[f.key] || ''}
              onChange={e => setDraft(prev => ({ ...prev, [f.key]: e.target.value }))}
              rows={2}
            />
          ) : (
            <input
              type="text"
              className="rse-profile-edit-field__input"
              value={draft[f.key] || ''}
              onChange={e => setDraft(prev => ({ ...prev, [f.key]: e.target.value }))}
            />
          )}
        </label>
      ))}
      <div className="rse-profile-edit-actions">
        <button type="button" className="btn rse-profile-edit-btn" onClick={() => setEditing(false)}>Cancel</button>
        <button type="button" className="btn rse-profile-edit-btn rse-profile-edit-btn--save" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ── Component ───────────────────────────────────────────────────────────────

function RealmSchemaEditor({
  verbCaps = {},
  scopeFilters = {},
  schemas = [],
  availableNodes = [],
  schemaGrants = null,
  onSchemaGrantsChange,
  allowedPluginIds = [],
  onAllowedPluginIdsChange,
  availablePlugins = [],
  // { "<pluginId>": { "<toolId>": "allow" | "ask" | "deny" } }
  //
  // A plugin is not a capability. Google is calendar, contacts, mail, files and
  // watch history, and "may this app use Google" is not a question anybody can
  // answer honestly — so the thing granted has to be the thing done. Absent
  // here means "no opinion", and the plugin grant stands: that is what kept
  // every realm written before tools existed working unchanged.
  toolModes = {},
  onToolModesChange,
  onChange,
  mode = 'edit',
  yamlPreview = '',
  onImportYaml = null,
  // Ceiling: realm ceiling for authorization/restrict-further flows.
  // Pills NOT in the ceiling are locked (dim, not clickable).
  ceilingVerbCaps = null,
  ceilingLabel = 'Realm ceiling',
  // Language overrides for different contexts
  emptyHint = 'no access granted',
  nodeFilterLabel = 'Filter Nodes by',
  // Opt-in: a host that already offers its own YAML view (the portal's Access
  // tab) would otherwise show two, side by side, doing the same thing. Default
  // false so every existing caller is unaffected.
  hideYamlToggle = false,
  // 'realm' (default) shows tags/nodes pickers; 'dlr' hides them (developer can't pick user tags/nodes)
  context = 'realm',
  // ── Consent mode props (DLR-driven auth page) ──
  dlrSchemaGroups,       // Array<{ schemaId, schemaName, name, ops: { R?: item, W?: item, ... }, anyRequired, items }>
  dlrNonNodeItems,       // Array<DlrItem> — relationships, plugins, discover, tags, activity
  dlrOpenAccessOps,      // { R?: item, W?: item, ... } — source='any' items
  consentDecisions,      // { [pillKey]: 'allow' | 'deny' }
  onConsentDecision,     // (key, item, decision) => void
  realmHasVerb,          // (resource, verb) => boolean
  dlrNodeCounts,         // { [schemaId]: { nodeIds, total, hasDataGap, hasLoaded } }
  activeScopePanel,      // schemaId | '__open_access' | null
  onScopeClick,          // (panelKey) => void
  renderScopePanel,      // (panelKey, schemaId) => ReactNode
  renderFieldGrants,     // (schemaName) => ReactNode
  renderVariables,       // () => ReactNode
  // ── Profile identity ──
  profileNodeId = null,
  onProfileNodeIdChange,
  onProfileUpdate,        // (nodeId, updates) => Promise — save edited profile fields
  profileNodes = [],
  defaultProfileNodeId = null,   // which one stands in when nothing names a face
  onEditProfile,                 // (nodeId) => void — send them to the Self page
  profileAvatarUrls = {},
}) {
  const isConsent = mode === 'consent';
  const isDlr = context === 'dlr';
  const isReadOnly = mode === 'readonly' || isConsent;
  // What "not set here" actually produces, named rather than left to be inferred.
  const defaultProfileName = (() => {
    const pn = profileNodes.find(p => String(p.id) === String(defaultProfileNodeId));
    return pn ? (pn.value_json?.display_name || pn.title || null) : null;
  })();

  // Compute which letters are allowed by the ceiling (for each VERB_MAP letter)
  const ceilingPerms = useMemo(() => {
    if (!ceilingVerbCaps) return null;
    const vc = normalizeVerbCaps(ceilingVerbCaps);
    const result = {};
    for (const [letter, { resource, verb }] of Object.entries(VERB_MAP)) {
      result[letter] = (vc[resource] || []).includes(verb);
    }
    return result;
  }, [ceilingVerbCaps]);

  const initial = useMemo(
    () => parseState(verbCaps, scopeFilters, schemas, [], availableNodes),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [globalPerms, setGlobalPerms]               = useState(initial.globalPerms);
  const [schemaRows, setSchemaRows]                 = useState(initial.schemaRows);
  const [nodeRows, setNodeRows]                     = useState(initial.nodeRows);
  const [advancedPerms, setAdvancedPerms]           = useState(initial.advancedPerms);
  const [activityCategories, setActivityCategories] = useState(initial.activityCategories);
  const [activityActorTypes, setActivityActorTypes] = useState(initial.activityActorTypes);

  const [showAllNodes, setShowAllNodes]     = useState(() => Object.values(initial.globalPerms).some(Boolean));
  const [expandedGrants, setExpandedGrants] = useState(new Set());

  const [showYaml, setShowYaml]                   = useState(false);
  const [toggleReady, setToggleReady]             = useState(false);
  const [yamlSubTab, setYamlSubTab]               = useState('view');
  const [yamlPasteText, setYamlPasteText]         = useState('');
  const [yamlImportErrors, setYamlImportErrors]   = useState([]);
  const [yamlImportSuccess, setYamlImportSuccess] = useState(false);

  // Schema picker
  const [showPicker, setShowPicker]     = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const pickerRef = useRef(null);

  // Node picker
  const [showNodePicker, setShowNodePicker]     = useState(false);
  const [nodePickerSearch, setNodePickerSearch] = useState('');
  const nodePickerRef = useRef(null);

  const emit = (gp, sr, nr, ap, ac = activityCategories, at = activityActorTypes) => {
    const { verb_caps, scope_filters } = toOutput(gp, sr, nr, ap, ac, at);
    onChange && onChange(verb_caps, scope_filters);
  };

  // Close schema picker on outside click
  useEffect(() => { setToggleReady(true); }, []);

  useEffect(() => {
    if (!showPicker) return;
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowPicker(false);
        setPickerSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showPicker]);

  // Close node picker on outside click
  useEffect(() => {
    if (!showNodePicker) return;
    const handler = (e) => {
      if (nodePickerRef.current && !nodePickerRef.current.contains(e.target)) {
        setShowNodePicker(false);
        setNodePickerSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showNodePicker]);

  const addedIds     = useMemo(() => new Set(schemaRows.map(r => r.id)), [schemaRows]);
  // Rows key off the pinned version id; picker entries carry the latest one.
  // Match on URI too, or a versioned schema offers itself again as addable.
  const addedUris    = useMemo(
    () => new Set(schemaRows.filter(r => !r.missing).map(r => r.uri).filter(Boolean)),
    [schemaRows]
  );
  const addedNodeIds = useMemo(() => new Set(nodeRows.map(r => r.id)), [nodeRows]);

  const pickerSchemas = useMemo(() => {
    const q = pickerSearch.toLowerCase();
    return schemas.filter(s => {
      if (addedIds.has(String(s.id)) || addedUris.has(s.schema_name || s.uri)) return false;
      if (!q) return true;
      return (
        (s.schema_name || s.uri || '').toLowerCase().includes(q)
      );
    });
  }, [schemas, addedIds, addedUris, pickerSearch]);

  const pickerNodes = useMemo(() => {
    const q = nodePickerSearch.toLowerCase();
    return (availableNodes || []).filter(n => {
      if (addedNodeIds.has(String(n.id))) return false;
      if (!q) return true;
      const title = n.value_json?.title || n.value_json?.name || '';
      return (
        title.toLowerCase().includes(q) ||
        String(n.id).includes(q) ||
        (n.schema_name || '').toLowerCase().includes(q)
      );
    });
  }, [availableNodes, addedNodeIds, nodePickerSearch]);

  // ── Mutators ──

  const applyPreset = (preset) => {
    const capped = Object.fromEntries(
      Object.entries(preset.perms).map(([k, v]) => [k, v && (!ceilingPerms || !!ceilingPerms[k])])
    );
    setGlobalPerms(capped);
    if (Object.values(capped).some(Boolean)) setShowAllNodes(true);

    // Build full advanced perms — start with all off, then apply preset's adv
    const newAdv = {};
    for (const section of ADVANCED_PERMS_DEF) {
      for (const { verb } of [...section.pills, ...(section.subPills || [])]) {
        newAdv[`${section.key}.${verb}`] = !!(preset.adv && preset.adv[`${section.key}.${verb}`]);
      }
    }
    setAdvancedPerms(newAdv);

    // Presets always mean "all tags / all activity categories / all actors"
    setActivityCategories([]);
    setActivityActorTypes([]);

    emit(capped, schemaRows, nodeRows, newAdv, [], []);
  };

  const toggleGlobal = (letter) => {
    // Block if ceiling forbids this verb
    if (ceilingPerms && !ceilingPerms[letter]) return;
    const newGp = { ...globalPerms, [letter]: !globalPerms[letter] };
    setGlobalPerms(newGp);
    emit(newGp, schemaRows, nodeRows, advancedPerms);
  };

  const toggleSchemaPerm = (rowId, letter) => {
    const newRows = schemaRows.map(r =>
      r.id === rowId ? { ...r, perms: { ...r.perms, [letter]: !r.perms[letter] } } : r
    );
    setSchemaRows(newRows);
    emit(globalPerms, newRows, nodeRows, advancedPerms);
  };

  const toggleNodePerm = (rowId, letter) => {
    const newRows = nodeRows.map(r =>
      r.id === rowId ? { ...r, perms: { ...r.perms, [letter]: !r.perms[letter] } } : r
    );
    setNodeRows(newRows);
    emit(globalPerms, schemaRows, newRows, advancedPerms);
  };

  const addSchema = (schema) => {
    const newRow = {
      id: String(schema.id),
      name: schemaLabel(schema.schema_name || schema.uri) || `Unknown schema ${String(schema.id).slice(0, 8)}…`,
      uri: schema.schema_name || schema.uri || String(schema.id),
      icon: schema.schema_metadata?.icon || null,
      perms: { R: true, W: false, E: false, D: false, P: false },
    };
    const newRows = [...schemaRows, newRow];
    setSchemaRows(newRows);
    setShowPicker(false);
    setPickerSearch('');
    // If global Read is on, turn it off — user is switching to selective mode
    const gp = globalPerms.R ? { ...globalPerms, R: false } : globalPerms;
    if (gp !== globalPerms) setGlobalPerms(gp);
    emit(gp, newRows, nodeRows, advancedPerms);
  };

  const addNode = (node) => {
    const newRow = {
      id: String(node.id),
      name: node.value_json?.title || node.value_json?.name || `Node #${node.id}`,
      schema_name: node.schema_name || null,
      perms: { R: true, W: false, E: false, D: false, P: false },
    };
    const newRows = [...nodeRows, newRow];
    setNodeRows(newRows);
    setShowNodePicker(false);
    setNodePickerSearch('');
    // If global Read is on, turn it off — user is switching to selective mode
    const gp = globalPerms.R ? { ...globalPerms, R: false } : globalPerms;
    if (gp !== globalPerms) setGlobalPerms(gp);
    emit(gp, schemaRows, newRows, advancedPerms);
  };

  const removeSchema = (id) => {
    const row = schemaRows.find(r => r.id === id);
    const newRows = schemaRows.filter(r => r.id !== id);
    setSchemaRows(newRows);
    setExpandedGrants(prev => { const s = new Set(prev); s.delete(id); return s; });
    if (row && schemaGrants?.[row.uri]) {
      const next = { ...schemaGrants };
      delete next[row.uri];
      onSchemaGrantsChange && onSchemaGrantsChange(Object.keys(next).length > 0 ? next : null);
    }
    emit(globalPerms, newRows, nodeRows, advancedPerms);
  };

  const removeNode = (id) => {
    const newRows = nodeRows.filter(r => r.id !== id);
    setNodeRows(newRows);
    emit(globalPerms, schemaRows, newRows, advancedPerms);
  };

  const toggleFieldsExpand = (id) => {
    setExpandedGrants(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const handleGrantChange = (uri, grantOrNull) => {
    const next = { ...(schemaGrants || {}) };
    if (grantOrNull === null) delete next[uri];
    else next[uri] = grantOrNull;
    onSchemaGrantsChange && onSchemaGrantsChange(Object.keys(next).length > 0 ? next : null);
  };

  const handleGrantRemove = (uri, rowId) => {
    const next = { ...(schemaGrants || {}) };
    delete next[uri];
    onSchemaGrantsChange && onSchemaGrantsChange(Object.keys(next).length > 0 ? next : null);
    setExpandedGrants(prev => { const s = new Set(prev); s.delete(rowId); return s; });
  };


  const toggleAdvanced = (k) => {
    const newAp = { ...advancedPerms, [k]: !advancedPerms[k] };
    setAdvancedPerms(newAp);
    if (k === 'plugins.execute' && !newAp[k] && allowedPluginIds?.length > 0) {
      onAllowedPluginIdsChange && onAllowedPluginIdsChange([]);
    }
    if (k === 'activity.read' && !newAp[k]) {
      setActivityCategories([]);
      setActivityActorTypes([]);
      emit(globalPerms, schemaRows, nodeRows, newAp, [], []);
    } else {
      emit(globalPerms, schemaRows, nodeRows, newAp);
    }
  };

  const togglePlugin = (pluginId) => {
    const current = allowedPluginIds || [];
    const allIds = availablePlugins.map(p => String(p.id));
    let next;
    if (current.length === 0) {
      // All selected → deselect this one
      next = allIds.filter(id => id !== pluginId);
    } else if (current.includes(pluginId)) {
      next = current.filter(id => id !== pluginId);
    } else {
      next = [...current, pluginId];
      if (next.length >= allIds.length) next = [];
    }
    onAllowedPluginIdsChange && onAllowedPluginIdsChange(next);
  };

  // THREE STATES, NOT TWO. A binary grant forces a tool like "send mail" into
  // whichever of allow/deny is wrong — deny and the feature is dead, allow and
  // nobody is asked. `ask` is the answer most real tools deserve.
  const TOOL_MODES = [
    { id: 'allow', label: 'allow', hint: 'run this without asking' },
    { id: 'ask',   label: 'ask',   hint: 'ask me each time' },
    { id: 'deny',  label: 'deny',  hint: 'never run this' },
  ];

  const toolModeFor = (pluginId, toolId) =>
    (toolModes?.[String(pluginId)] || {})[String(toolId)] || 'allow';

  const setToolMode = (pluginId, toolId, mode) => {
    const pid = String(pluginId);
    const next = { ...(toolModes || {}) };
    const forPlugin = { ...(next[pid] || {}) };
    forPlugin[String(toolId)] = mode;
    // An all-allow map says nothing the plugin grant did not, and storing it
    // would freeze the tool list — a tool published tomorrow would be denied
    // against a grant the person believed covered the plugin. So it is dropped
    // rather than kept as a redundant record of clicking around.
    if (Object.values(forPlugin).every(m => m === 'allow')) delete next[pid];
    else next[pid] = forPlugin;
    onToolModesChange && onToolModesChange(next);
  };

  const toolsOf = (plugin) => {
    const t = plugin?.tools;
    return Array.isArray(t) ? t.filter(x => x && x.tool_id) : [];
  };

  const toggleActivityCategory = (catId) => {
    const allIds = ACTIVITY_CATEGORIES.map(c => c.id);
    let next;
    if (activityCategories.length === 0) {
      // All selected → deselect this one
      next = allIds.filter(id => id !== catId);
    } else if (activityCategories.includes(catId)) {
      next = activityCategories.filter(c => c !== catId);
    } else {
      next = [...activityCategories, catId];
      // If all re-selected, collapse to empty (= all)
      if (next.length >= allIds.length) next = [];
    }
    setActivityCategories(next);
    emit(globalPerms, schemaRows, nodeRows, advancedPerms, next, activityActorTypes);
  };

  const toggleActivityActorType = (typeId) => {
    const allIds = ACTIVITY_ACTOR_TYPES.map(a => a.id);
    let next;
    if (activityActorTypes.length === 0) {
      next = allIds.filter(id => id !== typeId);
    } else if (activityActorTypes.includes(typeId)) {
      next = activityActorTypes.filter(t => t !== typeId);
    } else {
      next = [...activityActorTypes, typeId];
      if (next.length >= allIds.length) next = [];
    }
    setActivityActorTypes(next);
    emit(globalPerms, schemaRows, nodeRows, advancedPerms, activityCategories, next);
  };

  const handleImport = async () => {
    if (!onImportYaml || !yamlPasteText.trim()) return;
    setYamlImportErrors([]);
    const result = await onImportYaml(yamlPasteText);
    if (result?.errors?.length) {
      setYamlImportErrors(result.errors);
    } else {
      setYamlImportSuccess(true);
      setTimeout(() => {
        setShowYaml(false);
        setYamlSubTab('view');
        setYamlImportSuccess(false);
        setYamlPasteText('');
      }, 1200);
    }
  };

  // ── Quick-preset detection ──
  const hasFilters   = schemaRows.length > 0 || nodeRows.length > 0;
  const activePreset = QUICK_PRESETS.find(p => {
    const permsMatch = Object.entries(p.perms).every(([k, v]) => globalPerms[k] === v);
    const advMatch = ADVANCED_PERMS_DEF.every(section => {
      const allPills = [...section.pills, ...(section.subPills || [])];
      return allPills.every(({ verb }) => {
        const key = `${section.key}.${verb}`;
        return !!(p.adv && p.adv[key]) === !!advancedPerms[key];
      });
    });
    return permsMatch && advMatch;
  })?.key ?? null;

  // ── Derived section-active flags (for strikethrough titles) ──
  const actionsAnyOn = ADVANCED_PERMS_DEF.some(s => s.pills.some(p => advancedPerms[`${s.key}.${p.verb}`]));
  const identityGraphAnyOn = Object.values(globalPerms).some(Boolean) || schemaRows.length > 0 || nodeRows.length > 0;

  // ── Permission count for topbar summary ──
  const permCount = useMemo(() => {
    let count = 0;
    for (const letter of Object.keys(VERB_MAP)) {
      if (globalPerms[letter] ||
          schemaRows.some(r => r.perms[letter]) ||
          nodeRows.some(r => r.perms[letter])) count++;
    }
    count += Object.values(advancedPerms).filter(Boolean).length;
    return count;
  }, [globalPerms, schemaRows, nodeRows, advancedPerms]);

  // ── Section summaries (counts only — verbs shown by pills) ──
  const nodeSummary = useMemo(() => {
    const anyGlobal = Object.values(globalPerms).some(Boolean);
    const hasAny = anyGlobal || schemaRows.length > 0 || nodeRows.length > 0;
    if (!hasAny) return null;
    if (anyGlobal) return 'All nodes';
    const parts = [];
    if (schemaRows.length > 0) parts.push(`${schemaRows.length} schema${schemaRows.length !== 1 ? 's' : ''}`);
    if (nodeRows.length > 0) parts.push(`${nodeRows.length} node${nodeRows.length !== 1 ? 's' : ''}`);
    return parts.join(', ');
  }, [globalPerms, schemaRows, nodeRows]);


  const actionsSummary = useMemo(() => {
    let count = 0;
    for (const section of ADVANCED_PERMS_DEF) {
      count += section.pills.filter(p => advancedPerms[`${section.key}.${p.verb}`]).length;
    }
    return count > 0 ? `${count} enabled` : null;
  }, [advancedPerms]);

  // ── YAML panel ──────────────────────────────────────────────────────────

  // ── Consent mode helpers ──────────────────────────────────────────────────
  // Trigger verb mappings for consent mode — maps the canonical request type to [resource, verb].
  const CONSENT_TRIGGER = useMemo(() => ({
    'nodes:read': ['nodes', 'read'],
    'nodes:create': ['nodes', 'create'],
    'nodes:edit': ['nodes', 'edit'],
    'nodes:delete': ['nodes', 'delete'],
    'nodes:propose': ['nodes', 'propose'],
    'plugins:execute': ['plugins', 'execute'],
    // `discovery` is split into independent sub-pills (friends, groups); each maps to its own verb.
    // Bare `discovery` is kept as a fallback for callers that don't pass a sub-key.
    discovery: ['discovery', 'friends'],
    'discovery.friends': ['discovery', 'friends'],
    'discovery.groups': ['discovery', 'groups'],
    'discovery.realms': ['discovery', 'realms'],
    'activity:read': ['activity', 'read'],
  }), []);

  const consentPillOn = useCallback((pillKey, item, triggerKey = null) => {
    if (!item) return false;
    const tv = CONSENT_TRIGGER[triggerKey || requestTypeOf(item)];
    // If no realmHasVerb provided (e.g. DLR preview), assume all verbs available
    const fromRealm = tv && realmHasVerb ? realmHasVerb(tv[0], tv[1]) : true;
    const blocked = !fromRealm && !!tv;
    if (blocked) return false;
    const decision = consentDecisions?.[pillKey];
    if (decision === 'deny') return false;
    if (decision === 'allow') return true;
    return fromRealm;
  }, [CONSENT_TRIGGER, consentDecisions, realmHasVerb]);

  // ── Unified render ───────────────────────────────────────────────────────

  // ── Consent mode early return ────────────────────────────────────────────
  if (isConsent) {
    // Legacy DLRs stored before the `relationships` resource was retired may still
    // carry those request types. Filter them out of `otherItems` so they do not render
    // as unknown permissions — there is nothing left to grant. An app's stored DLR is
    // migrated separately; this keeps the UI honest meanwhile.
    const RETIRED_REL_TYPES = new Set([
      'relationships:read', 'relationships:create', 'relationships:edit',
      'relationships:delete', 'relationships:inherit',
    ]);
    const otherItems = (dlrNonNodeItems || []).filter(i => !RETIRED_REL_TYPES.has(requestTypeOf(i)));

    // Consent perm count
    let cPermCount = 0;
    for (const group of (dlrSchemaGroups || [])) {
      for (const [letter, item] of Object.entries(group.ops || {})) {
        if (consentPillOn(`${group.schemaId}__${letter}`, item)) cPermCount++;
      }
    }
    for (const [, item] of Object.entries(dlrOpenAccessOps || {})) {
      if (consentPillOn(item._key || requestTypeOf(item), item)) cPermCount++;
    }
    for (const item of otherItems) {
      if (consentPillOn(item._key || requestTypeOf(item), item)) cPermCount++;
    }

    // Pill labels for other action request types
    const OTHER_PILL_MAP = {
      run_plugins:  { label: 'Run Plugins',      tooltip: 'Execute plugins on your data' },
      discover:     { label: 'Social Discovery',  tooltip: 'Discover friends and groups' },
      see_activity: { label: 'Activity Log',      tooltip: 'Read your activity feed' },
    };

    const modeToggle = (
      <div className="rse-mode-toggle-footer">
        <div className={`rse-mode-toggle${showYaml ? ' rse-mode-toggle--yaml' : ''}${toggleReady ? ' rse-mode-toggle--ready' : ''}`}>
          <div className="rse-mode-toggle__indicator" />
          <button type="button" className={`btn rse-mode-toggle__btn${!showYaml ? ' rse-mode-toggle__btn--active' : ''}`} onClick={() => setShowYaml(false)}>Visual</button>
          <button type="button" className={`btn rse-mode-toggle__btn${showYaml ? ' rse-mode-toggle__btn--active' : ''}`} onClick={() => setShowYaml(true)}>YAML</button>
        </div>
      </div>
    );

    return (
      <div className="rse-root">
        {showYaml ? (
          <div className="rse-yaml-body">
            <YamlPreview yaml={yamlPreview} title="Authorization YAML" collapsible={false} defaultExpanded />
          </div>
        ) : (
        <>
        {/* ── Identity Graph (Node access + Relationships) ── */}
        <div className="rse-section">
          <div className="rse-section-title">
            <Network size={14} className="rse-section-title__icon" />
            Identity Graph
          </div>

          {/* Node access heading */}
          <div className="rse-select-nodes-heading" data-qh="rse-node-access">
            Node access
          </div>

          {/* Schema groups */}
          <div className="rse-select-nodes-group">
            {(dlrSchemaGroups || []).map(group => {
              const OPS_ORDER = ['R', 'W', 'E', 'D', 'P'];
              const hasRequiredBlocked = (group.items || []).some(item => {
                if (item.required === false) return false;
                const tv = CONSENT_TRIGGER[requestTypeOf(item)];
                return tv && realmHasVerb && !realmHasVerb(tv[0], tv[1]);
              });
              const nc = dlrNodeCounts?.[group.schemaId];
              const hasDataGap = nc?.hasDataGap;
              const isPickerOpen = activeScopePanel === `__picker_${group.schemaId}`;
              const hasExistingNodeOps = ['R', 'E', 'D'].some(op => {
                const item = group.ops[op];
                return item && consentPillOn(`${group.schemaId}__${op}`, item);
              });

              return (
                <React.Fragment key={group.schemaId}>
                  <div className={`rse-row rse-row--sub${hasRequiredBlocked || hasDataGap ? ' rse-row--consent-warn' : ''}`}>
                    <span className="rse-row-name" title={group.schemaName || group.name}>
                      <SchemaIcon iconName={schemas.find(s => String(s.id) === String(group.schemaId))?.schema_metadata?.icon} size={14} style={{ marginRight: 5, opacity: 0.7 }} />
                      {group.name}
                      {group.anyRequired && <span className="rse-consent-required">required</span>}
                      {group.schemaName && schemaGrants?.[group.schemaName]?.fields?.length > 0 && (
                        <span className="rse-consent-fields-badge" title={`Fields: ${schemaGrants[group.schemaName].fields.join(', ')}`}>
                          {schemaGrants[group.schemaName].fields.length} field{schemaGrants[group.schemaName].fields.length !== 1 ? 's' : ''} only
                        </span>
                      )}
                    </span>
                    <div className="rse-pills">
                      {OPS_ORDER.map(letter => {
                        const item = group.ops[letter];
                        if (!item) {
                          return (
                            <button type="button" key={letter} className="btn rse-pill rse-pill--not-requested" disabled title={`${PILL_TOOLTIPS[letter]} (not requested)`}>
                              {VERB_MAP[letter].label}
                            </button>
                          );
                        }
                        const sk = `${group.schemaId}__${letter}`;
                        const isRequired = item.required !== false;
                        const blocked = ceilingPerms && !ceilingPerms[letter];
                        const tvNode = CONSENT_TRIGGER[requestTypeOf(item)];
                        const realmBlocked = tvNode && realmHasVerb ? !realmHasVerb(tvNode[0], tvNode[1]) : false;
                        const isOn = consentPillOn(sk, item);
                        return (
                          <button
                            type="button"
                            key={letter}
                            className={`btn ${[
                              'rse-pill',
                              isOn ? 'rse-pill--on' : '',
                              (blocked || realmBlocked) ? 'rse-pill--ceiling-blocked' : '',
                              (blocked || realmBlocked) ? 'rse-pill--readonly' : '',
                            ].filter(Boolean).join(' ')}`}
                            title={realmBlocked
                              ? `${PILL_TOOLTIPS[letter]} — not available in this realm`
                              : `${PILL_TOOLTIPS[letter]}${isRequired ? ' (required)' : ' (optional)'}`}
                            onClick={() => {
                              if (realmBlocked || blocked) return;
                              onConsentDecision?.(sk, item, isOn ? 'deny' : 'allow');
                            }}
                          >
                            {VERB_MAP[letter].label}
                          </button>
                        );
                      })}
                      {hasExistingNodeOps && onScopeClick && (
                        <button
                          type="button"
                          className={`btn ${[
                            'rse-consent-scope',
                            isPickerOpen ? 'rse-consent-scope--narrowed' : '',
                            (nc?.nodeIds?.length > 0 && nc.nodeIds.length < nc.total) ? 'rse-consent-scope--narrowed' : '',
                            hasDataGap ? 'rse-consent-scope--warn' : '',
                          ].filter(Boolean).join(' ')}`}
                          onClick={() => onScopeClick(`__picker_${group.schemaId}`)}
                        >
                          {isPickerOpen ? 'Select...' : (
                            hasDataGap ? (nc?.total === 0 ? 'No nodes' : `${nc?.total || 0} of ${nc?.min || 1} needed`) :
                            !nc?.nodeIds?.length || (nc.total > 0 && nc.nodeIds.length === nc.total)
                              ? 'All (incl. future)'
                              : `${nc.nodeIds.length} of ${nc.total}`
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  {/* Realm-blocked warning */}
                  {hasRequiredBlocked && (
                    <div className="rse-consent-warn-text">
                      Required permissions not supported by this realm
                    </div>
                  )}
                  {hasDataGap && (
                    <div className="rse-consent-warn-text">
                      {nc?.total === 0
                        ? `No ${group.name} nodes in this realm`
                        : `Need at least ${nc?.min || 1} ${group.name} node${(nc?.min || 1) !== 1 ? 's' : ''} — only ${nc?.total || 0} available`}
                    </div>
                  )}
                  {/* Node picker panel */}
                  {isPickerOpen && renderScopePanel?.(`__picker_${group.schemaId}`, group.schemaId)}
                  {/* Field grants */}
                  {group.schemaName && renderFieldGrants?.(group.schemaName)}
                </React.Fragment>
              );
            })}

            {/* Open access row */}
            {dlrOpenAccessOps && Object.keys(dlrOpenAccessOps).length > 0 && (() => {
              const openOps = dlrOpenAccessOps;
              const anyOpenOpOn = ['R', 'E', 'D'].some(op => {
                const item = openOps[op];
                return item && consentPillOn(item._key || requestTypeOf(item), item);
              });
              const ncOpen = dlrNodeCounts?.['__open_access'];
              const openGap = anyOpenOpOn && ncOpen?.hasLoaded && ncOpen?.total === 0;
              const openPickerOpen = activeScopePanel === '__open_access_picker';
              return (
                <>
                  <div className={`rse-row rse-row--sub rse-row--open-access${openGap ? ' rse-row--consent-warn' : ''}`} style={{ marginTop: 8 }}>
                    <span className="rse-row-name">Any of your other data</span>
                    <div className="rse-pills">
                      {['R', 'W', 'E', 'D', 'P'].map(opLetter => {
                        const item = openOps[opLetter];
                        if (!item) return (
                          <button type="button" key={opLetter} className="btn rse-pill rse-pill--not-requested" disabled title={`${PILL_TOOLTIPS[opLetter]} (not requested)`}>
                            {VERB_MAP[opLetter].label}
                          </button>
                        );
                        const key = item._key || requestTypeOf(item);
                        const isOn = consentPillOn(key, item);
                        const tv = CONSENT_TRIGGER[requestTypeOf(item)];
                        const realmBlocked = tv && realmHasVerb ? !realmHasVerb(tv[0], tv[1]) : false;
                        return (
                          <button
                            type="button"
                            key={opLetter}
                            className={`btn ${[
                              'rse-pill',
                              isOn ? 'rse-pill--on' : '',
                              realmBlocked ? 'rse-pill--ceiling-blocked' : '',
                              realmBlocked ? 'rse-pill--readonly' : '',
                            ].filter(Boolean).join(' ')}`}
                            title={PILL_TOOLTIPS[opLetter]}
                            onClick={() => {
                              if (realmBlocked) return;
                              onConsentDecision?.(key, item, isOn ? 'deny' : 'allow');
                            }}
                          >
                            {VERB_MAP[opLetter].label}
                          </button>
                        );
                      })}
                      {anyOpenOpOn && onScopeClick && (
                        <button
                          type="button"
                          className={`btn ${[
                            'rse-consent-scope',
                            openPickerOpen ? 'rse-consent-scope--narrowed' : '',
                            openGap ? 'rse-consent-scope--warn' : '',
                          ].filter(Boolean).join(' ')}`}
                          onClick={() => onScopeClick('__open_access_picker')}
                        >
                          {openPickerOpen ? 'Narrow...' : (
                            openGap ? 'No nodes' :
                            ncOpen?.nodeIds?.length > 0 ? `${ncOpen.nodeIds.length} nodes` : 'Select nodes'
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  {openGap && <div className="rse-consent-warn-text">No other nodes in this realm</div>}
                  {openPickerOpen && renderScopePanel?.('__open_access_picker', null)}
                </>
              );
            })()}
          </div>

        </div>

        {/* ── Actions — one sub-section per type, matching edit mode ── */}
        {(() => {
          const pluginItem = otherItems.find(i => requestTypeOf(i) === 'plugins:execute');
          const discoverItem = otherItems.find(i => requestTypeOf(i) === 'discovery');
          const activityItem = otherItems.find(i => requestTypeOf(i) === 'activity:read');
          if (!pluginItem && !discoverItem && !activityItem) return null;

          const renderActionPill = (item, label, tooltip, subKey = null) => {
            if (!item) return (
              <button type="button" className="btn rse-rel-pill rse-pill--not-requested" disabled title={`${tooltip} (not requested)`}>
                {label}
              </button>
            );
            const baseKey = item._key || requestTypeOf(item);
            const key = subKey ? `${baseKey}__${subKey}` : baseKey;
            const triggerKey = subKey ? `${requestTypeOf(item)}.${subKey}` : requestTypeOf(item);
            const isOn = consentPillOn(key, item, triggerKey);
            const tv = CONSENT_TRIGGER[triggerKey];
            const realmBlocked = tv && realmHasVerb ? !realmHasVerb(tv[0], tv[1]) : false;
            return (
              <button
                type="button"
                className={`btn ${[
                  'rse-rel-pill',
                  isOn ? 'rse-rel-pill--on' : '',
                  realmBlocked ? 'rse-rel-pill--readonly' : '',
                ].filter(Boolean).join(' ')}`}
                title={realmBlocked ? `${tooltip} — not available in this realm` : tooltip}
                onClick={() => {
                  if (realmBlocked) return;
                  onConsentDecision?.(key, item, isOn ? 'deny' : 'allow', subKey);
                }}
              >
                {label}
              </button>
            );
          };

          return (
            <div className="rse-section">
            {/* Plugins */}
            <div className="rse-select-nodes-heading rse-select-nodes-heading--sep" data-qh="rse-plugins">
              Plugins
            </div>
            <div className="rse-select-nodes-group">
              <div className="rse-other-row rse-other-row--rel">
                <div className="rse-rel-pills">
                  {renderActionPill(pluginItem, 'Run', 'Execute plugins on your data')}
                </div>
                {pluginItem?.required !== false && pluginItem && <span className="rse-consent-required">required</span>}
              </div>
              {pluginItem && consentPillOn(pluginItem._key || requestTypeOf(pluginItem), pluginItem) && pluginItem.plugin_ids?.length > 0 && (
                <div className="rse-activity-detail">
                  <div className="rse-activity-group">
                    <span className="rse-activity-group-label">Specific plugins</span>
                    <div className="rse-rel-pills" style={{ flexWrap: 'wrap' }}>
                      {pluginItem.plugin_ids.map(id => {
                        const plug = availablePlugins.find(p => String(p.id) === String(id));
                        return (
                          <span key={id} className="rse-rel-pill rse-rel-pill--readonly rse-rel-pill--on">{plug ? plug.name : id}</span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Social Discovery */}
            <div className="rse-select-nodes-heading rse-select-nodes-heading--sep" data-qh="rse-discovery">
              Social Discovery
            </div>
            <div className="rse-select-nodes-group">
              <div className="rse-other-row rse-other-row--rel">
                <div className="rse-rel-pills">
                  {renderActionPill(
                    discoverItem && (discoverItem.friends !== false) ? discoverItem : null,
                    'Friends', 'Discover your connections', 'friends'
                  )}
                  {renderActionPill(
                    discoverItem && (discoverItem.groups !== false) ? discoverItem : null,
                    'Groups', 'Discover groups you belong to', 'groups'
                  )}
                  {renderActionPill(
                    discoverItem && (discoverItem.realms === true) ? discoverItem : null,
                    'Realms', 'Discover realms you make discoverable', 'realms'
                  )}
                </div>
                {discoverItem?.required !== false && discoverItem && <span className="rse-consent-required">required</span>}
              </div>
            </div>

            {/* Activity Log */}
            <div className="rse-select-nodes-heading rse-select-nodes-heading--sep" data-qh="rse-activity">
              Activity Log
            </div>
            <div className="rse-select-nodes-group">
              <div className="rse-other-row rse-other-row--rel">
                <div className="rse-rel-pills">
                  {renderActionPill(activityItem, 'Read', 'Read your activity feed')}
                </div>
                {activityItem?.required !== false && activityItem && <span className="rse-consent-required">required</span>}
              </div>
              {activityItem && consentPillOn(activityItem._key || requestTypeOf(activityItem), activityItem) && (
                <div className="rse-activity-detail">
                  <div className="rse-activity-group">
                    <div className="rse-activity-group-header">
                      <span className="rse-activity-group-label">Event types</span>
                      <span className="rse-activity-group-hint">
                        {!activityItem.categories?.length
                          ? 'All event types'
                          : `${activityItem.categories.length} of ${ACTIVITY_CATEGORIES.length} types`}
                      </span>
                    </div>
                    <div className="rse-rel-pills" style={{ flexWrap: 'wrap' }}>
                      {ACTIVITY_CATEGORIES.map(({ id, label }) => {
                        const isOn = !activityItem.categories?.length || activityItem.categories.includes(id);
                        return (
                          <span key={id} className={`rse-rel-pill rse-rel-pill--readonly${isOn ? ' rse-rel-pill--on' : ''}`}>{label}</span>
                        );
                      })}
                    </div>
                  </div>
                  <div className="rse-activity-group">
                    <div className="rse-activity-group-header">
                      <span className="rse-activity-group-label">Filter by actor</span>
                      <span className="rse-activity-group-hint">
                        {!activityItem.actor_types?.length
                          ? 'All actors'
                          : `${activityItem.actor_types.length} of ${ACTIVITY_ACTOR_TYPES.length} actors`}
                      </span>
                    </div>
                    <div className="rse-rel-pills" style={{ flexWrap: 'wrap' }}>
                      {ACTIVITY_ACTOR_TYPES.map(({ id, label }) => {
                        const isOn = !activityItem.actor_types?.length || activityItem.actor_types.includes(id);
                        return (
                          <span key={id} className={`rse-rel-pill rse-rel-pill--readonly${isOn ? ' rse-rel-pill--on' : ''}`}>{label}</span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
            </div>
          );
        })()}

        {/* Variables slot */}
        {renderVariables && (
          <div className="rse-consent-variables">
            {renderVariables()}
          </div>
        )}
        </>
        )}
        {modeToggle}
      </div>
    );
  }

  return (
    <div className="rse-root">
      {/* Topbar: always mounted so the toggle animates in place */}
      <div className="rse-topbar">
        <span className={`rse-perm-count${permCount > 0 ? ' rse-perm-count--active' : ''}`}>
          {permCount > 0 ? (
            <>
              {permCount} {isDlr ? 'request' : 'permission'}{permCount !== 1 ? 's' : ''}
              {activePreset && ` · ${QUICK_PRESETS.find(p => p.key === activePreset)?.label}`}
            </>
          ) : isDlr ? 'no requests' : 'no permissions'}
        </span>
        {!hideYamlToggle && (
          <div className={`rse-mode-toggle${showYaml ? ' rse-mode-toggle--yaml' : ''}${toggleReady ? ' rse-mode-toggle--ready' : ''}`}>
            <div className="rse-mode-toggle__indicator" />
            <button type="button" className={`btn rse-mode-toggle__btn${!showYaml ? ' rse-mode-toggle__btn--active' : ''}`} onClick={() => { setShowYaml(false); setYamlSubTab('view'); }}>Visual</button>
            <button type="button" className={`btn rse-mode-toggle__btn${showYaml ? ' rse-mode-toggle__btn--active' : ''}`} onClick={() => setShowYaml(true)}>YAML</button>
          </div>
        )}
      </div>

      {showYaml ? (
        <div className="rse-yaml-body">
          {onImportYaml && (
            <div className="rse-yaml-subtabs">
              <button
                type="button"
                className={`btn rse-yaml-subtab${yamlSubTab === 'view' ? ' rse-yaml-subtab--active' : ''}`}
                onClick={() => setYamlSubTab('view')}
              >View</button>
              <button
                type="button"
                className={`btn rse-yaml-subtab${yamlSubTab === 'paste' ? ' rse-yaml-subtab--active' : ''}`}
                onClick={() => setYamlSubTab('paste')}
              >Paste & Import</button>
            </div>
          )}
          {yamlSubTab === 'view' ? (
            <YamlPreview yaml={yamlPreview} title="Realm YAML" collapsible={false} defaultExpanded />
          ) : (
            <div className="rse-yaml-paste-wrap">
              <textarea
                className={`dev-dash__yaml-paste${yamlImportErrors.length > 0 ? ' dev-dash__yaml-paste--error' : ''}`}
                placeholder="Paste realm YAML here…"
                value={yamlPasteText}
                onChange={e => {
                  setYamlPasteText(e.target.value);
                  setYamlImportErrors([]);
                  setYamlImportSuccess(false);
                }}
              />
              {yamlImportSuccess && (
                <div className="dev-dash__yaml-success">Imported! Switching to visual editor…</div>
              )}
              {yamlImportErrors.length > 0 && (
                <div className="dev-dash__yaml-errors">
                  <div className="dev-dash__yaml-errors-title">Parse errors:</div>
                  <ul className="dev-dash__yaml-errors-list">
                    {yamlImportErrors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
              <div className="dev-dash__yaml-paste-actions">
                <button
                  type="button"
                  className={`btn dev-dash__yaml-import-btn${!yamlPasteText.trim() ? ' dev-dash__yaml-import-btn--disabled' : ''}`}
                  onClick={handleImport}
                  disabled={!yamlPasteText.trim()}
                >
                  Validate & Import
                </button>
                <button
                  type="button"
                  className="btn dev-dash__yaml-clear-btn"
                  onClick={() => { setYamlPasteText(''); setYamlImportErrors([]); setYamlImportSuccess(false); }}
                >
                  Clear
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
      <>

      {/* Templates strip — realm presets only (not shown for DLR context) */}
      {!isReadOnly && !isDlr && (
        <div className="rse-action-row">
          <div className="rse-action-row__label">
            <SlidersHorizontal size={15} strokeWidth={1.5} />
            Templates
          </div>
          <div className="rse-rel-pills">
            {QUICK_PRESETS.map(p => (
              <button
                type="button"
                key={p.key}
                className={`btn rse-rel-pill${activePreset === p.key ? ' rse-rel-pill--on' : ''}`}
                onClick={() => applyPreset(p)}
              >
                {p.label}
              </button>
            ))}
            {!activePreset && permCount > 0 && (
              <span className="rse-rel-pill rse-rel-pill--on">Customized</span>
            )}
          </div>
        </div>
      )}

      {/* ── Profile Identity ──
          SHOWN EVEN WITH NOTHING TO PICK. Hiding the section when the owner has no
          identities made the control disappear for exactly the person who needed
          telling — a realm that proposes no face is a fact about the realm, and
          "you have none yet" is the answer, not an empty screen. */}
      {onProfileNodeIdChange && (
        <div className="rse-section">
          <div className="rse-section-title">
            <User size={14} className="rse-section-title__icon" />
            Profile
          </div>
          {profileNodes.length === 0 ? (
            <div className="rse-profile-body rse-profile-empty">
              No identities yet — apps reached through this realm will see the default,
              or your username if you have none. Make one in Self → Profile.
            </div>
          ) : (
          <div className="rse-profile-body">
            <p className="rse-profile-hint">
              Everything reached through this realm presents this identity.
            </p>
            <div className="rse-profile-pills">
              <button
                type="button"
                className={`btn rse-rel-pill${(!profileNodeId || String(profileNodeId) === String(defaultProfileNodeId)) ? ' rse-rel-pill--on' : ''} ${isReadOnly ? 'rse-rel-pill--readonly' : ''}`}
                onClick={() => !isReadOnly && onProfileNodeIdChange(null)}
              >
                {defaultProfileName
                  ? <>Default <span className="rse-profile-pill__sub">· {defaultProfileName}</span></>
                  : 'Default'}
              </button>
              {profileNodes.filter(pn => String(pn.id) !== String(defaultProfileNodeId)).map(pn => {
                const isSelected = String(profileNodeId) === String(pn.id);
                const name = pn.value_json?.display_name || pn.title || 'Unnamed';
                return (
                  <button
                    type="button"
                    key={pn.id}
                    className={`btn rse-rel-pill${isSelected ? ' rse-rel-pill--on' : ''} ${isReadOnly ? 'rse-rel-pill--readonly' : ''}`}
                    onClick={() => !isReadOnly && onProfileNodeIdChange(isSelected ? null : String(pn.id))}
                  >
                    {name}
                  </button>
                );
              })}
            </div>
            {/* The card shows the identity this realm actually PRESENTS, which is the
                pinned one or — when nothing is pinned — the default. Gating it on the
                pin alone meant choosing "Default · Tej" showed nothing at all, as if
                the realm presented nobody. */}
            {(profileNodeId || defaultProfileNodeId) && (() => {
              const shownId = profileNodeId || defaultProfileNodeId;
              const pn = profileNodes.find(p => String(p.id) === String(shownId));
              if (!pn) return null;
              const vj = pn.value_json || {};
              const displayName = vj.display_name || pn.title || '?';
              const initial = displayName.charAt(0).toUpperCase();
              const avatarUrl = profileAvatarUrls[String(pn.id)];
              const profileGrant = schemaGrants?.['profile'];
              const grantedFields = profileGrant?.fields || null; // null = all fields shared
              const PROFILE_FIELDS = [
                { key: 'display_name', label: 'Display Name' },
                { key: 'first_name', label: 'First Name' },
                { key: 'last_name', label: 'Last Name' },
                { key: 'bio', label: 'Bio' },
                { key: 'pronouns', label: 'Pronouns' },
                { key: 'location', label: 'Location' },
                { key: 'avatar_file_id', label: 'Avatar' },
                { key: 'website', label: 'Website' },
              ];
              const EDITABLE_FIELDS = PROFILE_FIELDS.filter(f => f.key !== 'avatar_file_id');
              // Only show fields that actually have values on this profile
              const populatedFields = PROFILE_FIELDS.filter(f => vj[f.key]);
              const toggleField = (fieldKey) => {
                if (isReadOnly || !onSchemaGrantsChange) return;
                const current = grantedFields ? [...grantedFields] : populatedFields.map(f => f.key);
                const idx = current.indexOf(fieldKey);
                let next;
                if (idx >= 0) {
                  next = current.filter(k => k !== fieldKey);
                } else {
                  next = [...current, fieldKey];
                }
                const nextGrants = { ...(schemaGrants || {}) };
                if (next.length === 0 || next.length >= populatedFields.length) {
                  delete nextGrants['profile'];
                } else {
                  nextGrants['profile'] = { fields: next };
                }
                onSchemaGrantsChange(Object.keys(nextGrants).length > 0 ? nextGrants : null);
              };
              return (
                <>
                  <div className="rse-profile-preview">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt="" className="rse-profile-preview__avatar rse-profile-preview__avatar--img" />
                    ) : (
                      <div className="rse-profile-preview__avatar">{initial}</div>
                    )}
                    <div className="rse-profile-preview__info">
                      <div className="rse-profile-preview__name">{displayName}</div>
                      {vj.bio && (
                        <div className="rse-profile-preview__bio">{vj.bio}</div>
                      )}
                    </div>
                    {onEditProfile && !isReadOnly ? (
                      /* An identity is edited in ONE place — the Self page that owns
                         them. A second editor here meant two surfaces writing the same
                         node, and a name that could be changed from a screen about
                         permissions. */
                      <button
                        type="button"
                        className="btn rse-profile-edit"
                        onClick={() => onEditProfile(String(pn.id))}
                      >
                        Edit
                      </button>
                    ) : onProfileUpdate && !isReadOnly && (
                      <ProfileEditForm
                        nodeId={pn.id}
                        valueJson={vj}
                        fields={EDITABLE_FIELDS}
                        onSave={onProfileUpdate}
                      />
                    )}
                  </div>
                  {populatedFields.length > 0 && (
                    <div className="rse-profile-fields">
                      <div className="rse-profile-fields__label">Shared fields</div>
                      <div className="rse-profile-fields__pills">
                        {populatedFields.map(f => {
                          const isOn = !grantedFields || grantedFields.includes(f.key);
                          return (
                            <button
                              type="button"
                              key={f.key}
                              className={`btn rse-rel-pill rse-rel-pill--sub${isOn ? ' rse-rel-pill--on' : ''} ${isReadOnly ? 'rse-rel-pill--readonly' : ''}`}
                              onClick={() => toggleField(f.key)}
                            >
                              {f.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
          )}
        </div>
      )}

      {/* ── Identity Graph (Nodes + Relationships + Tags) ── */}
      <div className="rse-section">
        <div className="rse-section-title">
          <Network size={14} className="rse-section-title__icon" />
          Identity Graph
        </div>

        {/* Ceiling row — shown when a ceiling is provided (e.g. realm ceiling in auth flows) */}
        {ceilingPerms && (
          <div className="rse-row rse-row--ceiling">
            <span className="rse-row-name rse-row-name--ceiling">
              <span className="rse-ceiling-lock">⊘</span>
              {ceilingLabel}
            </span>
            <div className="rse-pills">
              {Object.keys(VERB_MAP).map(letter => (
                <button
                  type="button"
                  key={letter}
                  className={`btn rse-pill rse-pill--readonly${ceilingPerms[letter] ? ' rse-pill--ceiling-on' : ' rse-pill--ceiling-off'}`}
                  title={ceilingPerms[letter]
                    ? `${PILL_TOOLTIPS[letter]} — allowed by ${ceilingLabel}`
                    : `${PILL_TOOLTIPS[letter]} — not permitted by ${ceilingLabel}`}
                  disabled
                >
                  {VERB_MAP[letter].label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Node access ── */}
        {(!isReadOnly || showAllNodes || schemaRows.length > 0 || nodeRows.length > 0) && (<>
          <div className="rse-select-nodes-heading" data-qh={isDlr ? 'rse-dlr-node' : 'rse-node-access'}>
            Node access
            {nodeSummary && <span className="rse-subsection-summary">{nodeSummary}</span>}
          </div>
        </>)}

        <div className="rse-select-nodes-group">
          {/* ── All Nodes (global baseline) ── */}
          {showAllNodes && (
            <div className="rse-action-row">
              <div className="rse-action-row__label">
                <Layers size={15} strokeWidth={1.5} />
                All nodes
              </div>
              <div className="rse-rel-pills">
                {Object.keys(VERB_MAP).map(letter => {
                  const ceilingBlocked = ceilingPerms && !ceilingPerms[letter];
                  return (
                    <button
                      type="button"
                      key={letter}
                      className={`btn ${[
                        'rse-rel-pill',
                        globalPerms[letter] ? 'rse-rel-pill--on' : '',
                        ceilingBlocked ? 'rse-rel-pill--readonly' : '',
                        (isReadOnly || ceilingBlocked) ? 'rse-rel-pill--readonly' : '',
                      ].filter(Boolean).join(' ')}`}
                      title={ceilingBlocked
                        ? `${PILL_TOOLTIPS[letter]} — not permitted by ${ceilingLabel}`
                        : PILL_TOOLTIPS[letter]}
                      onClick={() => !isReadOnly && !ceilingBlocked && toggleGlobal(letter)}
                    >
                      {VERB_MAP[letter].label}
                    </button>
                  );
                })}
                {!isReadOnly && (
                  <button type="button" className="btn rse-rel-pill rse-rel-pill--remove" onClick={() => {
                    setShowAllNodes(false);
                    const cleared = { R: false, W: false, E: false, D: false, P: false };
                    setGlobalPerms(cleared);
                    emit(cleared, schemaRows, nodeRows, advancedPerms);
                  }} title="Remove">
                    <X size={10} />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ── By Schema ── */}
          {schemaRows.length > 0 && (
            <div className={`rse-filter-label${showAllNodes ? ' rse-filter-label--sep' : ''}`}>By Schema</div>
          )}

          {schemaRows.map(row => {
            const hasGrants = !!(schemaGrants?.[row.uri] && (
              (schemaGrants[row.uri].fields?.length > 0) ||
              (schemaGrants[row.uri].filter && Object.keys(schemaGrants[row.uri].filter).length > 0)
            ));
            const isExpanded = expandedGrants.has(row.id);
            return (
              <React.Fragment key={row.id}>
                <div className={`rse-row rse-row--sub${isExpanded ? ' rse-row--expanded' : ''}`}>
                  <span
                    className={`rse-row-name${row.missing ? ' rse-row-name--missing' : ''}`}
                    title={row.missing
                      ? `Schema ${row.id} is no longer available — the permission still applies to it`
                      : row.uri}
                  >
                    <SchemaIcon iconName={row.icon} size={14} style={{ marginRight: 5, opacity: 0.7 }} />
                    {row.name || row.uri}
                  </span>
                  <div className="rse-pills">
                    {Object.keys(VERB_MAP).map(letter => {
                      const isInherited = globalPerms[letter];
                      const isOn = row.perms[letter] || isInherited;
                      const ceilingBlocked = ceilingPerms && !ceilingPerms[letter];
                      return (
                        <button
                    type="button"
                          key={letter}
                          className={`btn ${[
                            'rse-pill',
                            isOn ? 'rse-pill--on' : '',
                            isInherited ? 'rse-pill--inherited' : '',
                            ceilingBlocked ? 'rse-pill--ceiling-blocked' : '',
                            (isReadOnly || isInherited || ceilingBlocked) ? 'rse-pill--readonly' : '',
                          ].filter(Boolean).join(' ')}`}
                          title={isInherited
                            ? `${PILL_TOOLTIPS[letter]} — inherited from All nodes`
                            : ceilingBlocked
                            ? `${PILL_TOOLTIPS[letter]} — not permitted by ${ceilingLabel}`
                            : PILL_TOOLTIPS[letter]}
                          onClick={() => !isReadOnly && !isInherited && !ceilingBlocked && toggleSchemaPerm(row.id, letter)}
                        >
                          {VERB_MAP[letter].label}
                        </button>
                      );
                    })}
                    <button
                    type="button"
                      className={`btn ${[
                        'rse-fields-btn',
                        (isExpanded || hasGrants) ? 'rse-fields-btn--active' : '',
                        isReadOnly ? 'rse-fields-btn--readonly' : '',
                      ].filter(Boolean).join(' ')}`}
                      onClick={() => !isReadOnly && toggleFieldsExpand(row.id)}
                      title={hasGrants
                        ? (isExpanded ? 'Hide field restrictions' : 'Field restrictions active — click to edit')
                        : (isExpanded ? 'Hide' : 'Add field restrictions')}
                      disabled={isReadOnly && !isExpanded}
                    >
                      <SlidersHorizontal size={11} />
                    </button>
                    {!isReadOnly ? (
                      <button type="button" className="btn rse-remove-btn" onClick={() => removeSchema(row.id)} title="Remove schema">
                        <X size={11} />
                      </button>
                    ) : (
                      <span className="rse-pill-spacer" />
                    )}
                  </div>
                </div>
                {isExpanded && !isReadOnly && (
                  <div className="rse-grant-card-wrap">
                    <DlrGrantCard
                      schemaName={row.uri}
                      grant={schemaGrants?.[row.uri] || {}}
                      onChange={handleGrantChange}
                      onRemove={(uri) => handleGrantRemove(uri, row.id)}
                    />
                  </div>
                )}
              </React.Fragment>
            );
          })}

          {/* ── By Custom (node IDs) — hidden in DLR mode ── */}
          {!isDlr && nodeRows.length > 0 && (
            <div className={`rse-filter-label${(showAllNodes || schemaRows.length > 0) ? ' rse-filter-label--sep' : ''}`}>By Node ID</div>
          )}

          {!isDlr && nodeRows.map(row => (
            <div key={row.id} className="rse-row rse-row--sub">
              <span className="rse-row-name" title={`Node #${row.id}${row.schema_name ? ` · ${row.schema_name}` : ''}`}>
                {row.name}
                {row.schema_name && (
                  <span className="rse-row-name-schema">{row.schema_name.split(':').pop()}</span>
                )}
              </span>
              <div className="rse-pills">
                {Object.keys(VERB_MAP).map(letter => {
                  // W (create_nodes) doesn't support node_ids — show as N/A
                  if (NODE_ID_UNSUPPORTED.has(letter)) {
                    return (
                      <button
                    type="button"
                        key={letter}
                        className="btn rse-pill rse-pill--na"
                        disabled
                        title="Create — not applicable for specific node restrictions"
                      >
                        {VERB_MAP[letter].label}
                      </button>
                    );
                  }
                  const isInherited = globalPerms[letter];
                  const isOn = row.perms[letter] || isInherited;
                  const ceilingBlocked = ceilingPerms && !ceilingPerms[letter];
                  return (
                    <button
                    type="button"
                      key={letter}
                      className={`btn ${[
                        'rse-pill',
                        isOn ? 'rse-pill--on' : '',
                        isInherited ? 'rse-pill--inherited' : '',
                        ceilingBlocked ? 'rse-pill--ceiling-blocked' : '',
                        (isReadOnly || isInherited || ceilingBlocked) ? 'rse-pill--readonly' : '',
                      ].filter(Boolean).join(' ')}`}
                      title={isInherited
                        ? `${PILL_TOOLTIPS[letter]} — inherited from All nodes`
                        : ceilingBlocked
                        ? `${PILL_TOOLTIPS[letter]} — not permitted by ${ceilingLabel}`
                        : PILL_TOOLTIPS[letter]}
                      onClick={() => !isReadOnly && !isInherited && !ceilingBlocked && toggleNodePerm(row.id, letter)}
                    >
                      {VERB_MAP[letter].label}
                    </button>
                  );
                })}
                <span className="rse-pill-spacer" />{/* spacer for field-restrictions slot */}
                {!isReadOnly ? (
                  <button type="button" className="btn rse-remove-btn" onClick={() => removeNode(row.id)} title="Remove node">
                    <X size={11} />
                  </button>
                ) : (
                  <span className="rse-pill-spacer" />
                )}
              </div>
            </div>
          ))}

          {/* ── Add buttons — grouped row ── */}
          {!isReadOnly && (
            <div className="rse-add-group">
              {!showAllNodes && (
                <>
                  <div className="rse-add-group-item">
                    <button type="button" className="btn rse-add-btn rse-add-btn--grouped" onClick={() => {
                      setShowAllNodes(true);
                      const newGp = { ...globalPerms, R: true };
                      setGlobalPerms(newGp);
                      emit(newGp, schemaRows, nodeRows, advancedPerms);
                    }}>
                      <Plus size={12} />
                      All Nodes
                    </button>
                  </div>
                  <div className="rse-add-group-item" ref={pickerRef}>
                    <button type="button" className="btn rse-add-btn rse-add-btn--grouped" onClick={() => { setShowPicker(!showPicker); setPickerSearch(''); }}>
                      <Plus size={12} />
                      By Schema
                    </button>
                    {showPicker && (
                      <div className="rse-picker-dropdown">
                        <input
                          className="rse-picker-search"
                          placeholder="Search schemas…"
                          value={pickerSearch}
                          onChange={e => setPickerSearch(e.target.value)}
                          autoFocus
                        />
                        <div className="rse-picker-list">
                          {pickerSchemas.length === 0 ? (
                            <div className="rse-picker-empty">
                              {pickerSearch ? 'No matches' : 'All schemas already added'}
                            </div>
                          ) : (
                            pickerSchemas.slice(0, 24).map(s => (
                              <button type="button" key={s.id} className="btn rse-picker-item" onClick={() => addSchema(s)}>
                                <span className="rse-picker-name">
                                  <SchemaIcon iconName={s.schema_metadata?.icon} size={13} style={{ marginRight: 5, opacity: 0.6, verticalAlign: -2 }} />
                                  {schemaLabel(s.schema_name || s.uri)}
                                </span>
                                <span className="rse-picker-uri">{s.schema_name || s.uri}</span>
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {!isDlr && (
                  <div className="rse-add-group-item" ref={nodePickerRef}>
                    <button type="button" className="btn rse-add-btn rse-add-btn--grouped" onClick={() => { setShowNodePicker(!showNodePicker); setNodePickerSearch(''); }}>
                      <Plus size={12} />
                      By Node ID
                    </button>
                    {showNodePicker && (
                      <div className="rse-picker-dropdown">
                        <input
                          className="rse-picker-search"
                          placeholder="Search nodes…"
                          value={nodePickerSearch}
                          onChange={e => setNodePickerSearch(e.target.value)}
                          autoFocus
                        />
                        <div className="rse-picker-list">
                          {pickerNodes.length === 0 ? (
                            <div className="rse-picker-empty">
                              {nodePickerSearch ? 'No matches' : (availableNodes?.length === 0 ? 'No nodes available' : 'All nodes already added')}
                            </div>
                          ) : (
                            pickerNodes.slice(0, 24).map(n => (
                              <button type="button" key={n.id} className="btn rse-picker-item" onClick={() => addNode(n)}>
                                <span className="rse-picker-name">
                                  {n.value_json?.title || n.value_json?.name || `Node #${n.id}`}
                                </span>
                                <span className="rse-picker-uri">{n.schema_name || `#${n.id}`}</span>
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* The Relationships section is gone with the `relationships` resource.
            It offered See / Create / Edit / Delete / Allow pills that a realm author
            could tick and that the backend can no longer honour — a consent surface
            promising something it cannot deliver.

            A relationship is a node on the `relationship` schema, so it is granted
            through the ordinary schema rows above; the one capability that needed
            its own control, "add to other people's posts", is now nodes:create
            scoped to the `belongs_to` schema and appears there.
 */}

      </div>{/* close rse-section: Identity Graph */}

      {/* ── Actions ── */}
      <div className="rse-section">
        <div className="rse-section-title">
          <Zap size={14} className="rse-section-title__icon" />
          Actions
        </div>

        {['discovery', 'activity', 'plugins', 'automations', 'sub_entity'].map(key => {
          const section = ADVANCED_PERMS_DEF.find(s => s.key === key);
          const isPlugins = section.key === 'plugins';
          const isActivity = section.key === 'activity';
          const pluginsOn = advancedPerms['plugins.execute'];
          const activityOn = advancedPerms['activity.read'];
          const hasSpecificPlugins = allowedPluginIds?.length > 0;
          const sectionQh = {
            plugins:    'rse-plugins',
            discovery:  'rse-discovery',
            activity:   'rse-activity',
            automations:'rse-automations',
            sub_entity: 'rse-sub-entity',
          };
          const sectionIcons = {
            plugins:    Puzzle,
            discovery:  Users,
            activity:   Activity,
            automations:Zap,
            sub_entity: Users,
          };
          const SectionIcon = sectionIcons[section.key];

          return (
            <React.Fragment key={section.key}>
              <div className="rse-action-row" data-qh={sectionQh[section.key]}>
                <span className="rse-action-row__label"><SectionIcon size={15} strokeWidth={1.6} /> {section.label}</span>
                <div className="rse-rel-pills">
                  {section.pills.map(({ verb, label, tooltip }) => {
                    const k = `${section.key}.${verb}`;
                    return (
                      <button
                        type="button"
                        key={verb}
                        className={`btn ${[
                          'rse-rel-pill',
                          advancedPerms[k] ? 'rse-rel-pill--on' : '',
                          isReadOnly ? 'rse-rel-pill--readonly' : '',
                        ].filter(Boolean).join(' ')}`}
                        title={tooltip}
                        onClick={() => !isReadOnly && toggleAdvanced(k)}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="rse-select-nodes-group">

              {/* Plugin picker — inside group */}
              {isPlugins && pluginsOn && (
                <div className="rse-plugin-picker-wrap">
                  {availablePlugins.length === 0 ? (
                    <span className="rse-plugin-picker-empty">
                      {isReadOnly
                        ? (hasSpecificPlugins ? `${allowedPluginIds.length} specific plugin${allowedPluginIds.length !== 1 ? 's' : ''} allowed` : 'All plugins allowed')
                        : 'No plugins registered — all plugins will be allowed'}
                    </span>
                  ) : (
                    <>
                      <span className="rse-plugin-picker-hint">
                        {hasSpecificPlugins
                          ? `${allowedPluginIds.length} of ${availablePlugins.length} plugins`
                          : 'All plugins'}
                      </span>
                      <div className="rse-plugin-chips">
                        {availablePlugins.map(p => {
                          const isSelected = !hasSpecificPlugins || (allowedPluginIds || []).includes(String(p.id));
                          return (
                            <button
                              type="button"
                              key={p.id}
                              className={`btn ${[
                                'rse-rel-pill',
                                isSelected ? 'rse-rel-pill--on' : '',
                                isReadOnly ? 'rse-rel-pill--readonly' : '',
                              ].filter(Boolean).join(' ')}`}
                              onClick={() => !isReadOnly && togglePlugin(String(p.id))}
                              title={p.description || p.name}
                            >
                              {p.name}
                            </button>
                          );
                        })}
                        {hasSpecificPlugins && !isReadOnly && (
                          <button
                            type="button"
                            className="btn rse-plugin-clear-btn"
                            onClick={() => onAllowedPluginIdsChange && onAllowedPluginIdsChange([])}
                          >
                            select all
                          </button>
                        )}
                      </div>
                      {/* PER-TOOL, because a plugin is not a capability.
                          Only for plugins this realm actually allows, and only
                          those declaring tools — a single-purpose plugin has
                          nothing to narrow and a row of controls would imply
                          otherwise. */}
                      <div className="rse-tool-grants">
                        {availablePlugins
                          .filter(p => !hasSpecificPlugins
                            || (allowedPluginIds || []).includes(String(p.id)))
                          .filter(p => toolsOf(p).length > 0)
                          .map(p => (
                            <div className="rse-tool-plugin" key={`tools_${p.id}`}>
                              <div className="rse-tool-plugin-head">
                                <span className="rse-tool-plugin-name">{p.name}</span>
                                <span className="rse-tool-plugin-count">
                                  {toolsOf(p).length} tools
                                </span>
                              </div>
                              {toolsOf(p).map(t => {
                                const mode = toolModeFor(p.id, t.tool_id);
                                return (
                                  <div className="rse-tool-row" key={`${p.id}_${t.tool_id}`}>
                                    <div className="rse-tool-label">
                                      <span className="rse-tool-name">
                                        {t.name || t.tool_id}
                                      </span>
                                      {t.description && (
                                        <span className="rse-tool-desc">{t.description}</span>
                                      )}
                                    </div>
                                    <div className="rse-tool-modes">
                                      {TOOL_MODES.map(m => (
                                        <button
                                          type="button"
                                          key={m.id}
                                          className={`btn rse-tool-mode${
                                            mode === m.id ? ' rse-tool-mode--on' : ''
                                          }${isReadOnly ? ' rse-tool-mode--readonly' : ''}`}
                                          aria-pressed={mode === m.id}
                                          title={m.hint}
                                          onClick={() => !isReadOnly
                                            && setToolMode(p.id, t.tool_id, m.id)}
                                        >
                                          {m.label}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Activity filters — inside group */}
              {isActivity && activityOn && (
                <div className="rse-activity-detail">
                  <div className="rse-activity-group">
                    <div className="rse-activity-group-header">
                      <span className="rse-activity-group-label">Event types</span>
                      <span className="rse-activity-group-hint">
                        {activityCategories.length === 0
                          ? 'All event types'
                          : `${activityCategories.length} of ${ACTIVITY_CATEGORIES.length} types`}
                      </span>
                    </div>
                    <div className="rse-plugin-chips">
                      {ACTIVITY_CATEGORIES.map(({ id, label }) => {
                        const isOn = activityCategories.length === 0 || activityCategories.includes(id);
                        return (
                          <button
                            type="button"
                            key={id}
                            className={`btn ${[
                              'rse-rel-pill',
                              isOn ? 'rse-rel-pill--on' : '',
                              isReadOnly ? 'rse-rel-pill--readonly' : '',
                            ].filter(Boolean).join(' ')}`}
                            onClick={() => !isReadOnly && toggleActivityCategory(id)}
                          >
                            {label}
                          </button>
                        );
                      })}
                      {activityCategories.length > 0 && !isReadOnly && (
                        <button
                          type="button"
                          className="btn rse-plugin-clear-btn"
                          onClick={() => {
                            setActivityCategories([]);
                            emit(globalPerms, schemaRows, nodeRows, advancedPerms, [], activityActorTypes);
                          }}
                        >
                          select all
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="rse-activity-group">
                    <div className="rse-activity-group-header">
                      <span className="rse-activity-group-label">Filter by actor</span>
                      <span className="rse-activity-group-hint">
                        {activityActorTypes.length === 0
                          ? 'All actors'
                          : `${activityActorTypes.length} of ${ACTIVITY_ACTOR_TYPES.length} actors`}
                      </span>
                    </div>
                    <div className="rse-plugin-chips">
                      {ACTIVITY_ACTOR_TYPES.map(({ id, label }) => {
                        const isOn = activityActorTypes.length === 0 || activityActorTypes.includes(id);
                        return (
                          <button
                            type="button"
                            key={id}
                            className={`btn ${[
                              'rse-rel-pill',
                              isOn ? 'rse-rel-pill--on' : '',
                              isReadOnly ? 'rse-rel-pill--readonly' : '',
                            ].filter(Boolean).join(' ')}`}
                            onClick={() => !isReadOnly && toggleActivityActorType(id)}
                          >
                            {label}
                          </button>
                        );
                      })}
                      {activityActorTypes.length > 0 && !isReadOnly && (
                        <button
                          type="button"
                          className="btn rse-plugin-clear-btn"
                          onClick={() => {
                            setActivityActorTypes([]);
                            emit(globalPerms, schemaRows, nodeRows, advancedPerms, activityCategories, []);
                          }}
                        >
                          select all
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
              </div>{/* close rse-select-nodes-group */}
            </React.Fragment>
          );
        })}
      </div>
      </>
    )}
    </div>
  );
}

export default RealmSchemaEditor;
